import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBasket, Plus, Search, ImageIcon, Loader2 } from "lucide-react";
import { useCart } from "./CartContext";
import CartDrawer from "./CartDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveImageUrl(p) {
  if (!p) return null;
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  return `${process.env.REACT_APP_BACKEND_URL}${p}`;
}

export default function StorePage() {
  const { add, count, setOpen } = useCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    Promise.all([axios.get(`${API}/products`), axios.get(`${API}/products/categories`)])
      .then(([p, c]) => {
        setProducts(p.data);
        setCategories(c.data);
      })
      .catch(() => toast.error("Erro ao carregar a loja"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchCat = !activeCat || p.category === activeCat;
      const matchQ =
        !search ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.description || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchQ;
    });
  }, [products, activeCat, search]);

  // Group filtered by category (only when no specific category active)
  const grouped = useMemo(() => {
    if (activeCat) return null;
    const map = {};
    filtered.forEach((p) => {
      (map[p.category] = map[p.category] || []).push(p);
    });
    return map;
  }, [filtered, activeCat]);

  return (
    <div data-testid="store-page" className="min-h-screen pt-24 pb-20 bg-[#F3F1EB]">
      <div className="container-custom">
        {/* Hero */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10 lg:mb-14"
        >
          <span className="font-accent text-2xl text-[#1B93A4] block">A nossa loja</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#3A4A38] leading-tight mt-2">
            Produtos frescos da quinta
          </h1>
          <p className="text-base text-[#2C2C2C]/70 mt-3 max-w-2xl">
            Escolha os produtos que pretende receber. Entregamos todas as
            terças-feiras à tarde na região de Braga e Vila Verde.
          </p>
        </motion.header>

        {/* Filters bar */}
        <div className="sticky top-20 z-20 bg-[#F3F1EB]/95 backdrop-blur-sm py-3 mb-6 border-b border-[#D4C4A8]/40">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2C2C2C]/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar..."
                data-testid="store-search"
                className="w-full pl-9 pr-3 py-2.5 rounded-full border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto flex-1">
              <CategoryChip active={!activeCat} onClick={() => setActiveCat("")}>
                Tudo
              </CategoryChip>
              {categories.map((c) => (
                <CategoryChip
                  key={c.key}
                  active={activeCat === c.key}
                  onClick={() => setActiveCat(c.key)}
                  testId={`store-category-${c.key}`}
                >
                  {c.label}
                </CategoryChip>
              ))}
            </div>
            <button
              onClick={() => setOpen(true)}
              data-testid="store-cart-btn"
              className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#3A4A38] text-white text-sm hover:bg-[#2C3A2A] transition-colors"
            >
              <ShoppingBasket size={16} />
              Cesto
              {count > 0 && (
                <span className="ml-1 bg-[#D4C4A8] text-[#3A4A38] text-xs font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Products */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#3A4A38]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <ImageIcon size={40} className="mx-auto text-[#2C2C2C]/30 mb-3" />
            <p className="text-sm text-[#2C2C2C]/60">
              {products.length === 0
                ? "Ainda não há produtos disponíveis. Estamos a preparar a loja!"
                : "Sem produtos para a pesquisa atual."}
            </p>
          </div>
        ) : grouped ? (
          <div className="space-y-12">
            {categories.map((c) => {
              const items = grouped[c.key];
              if (!items || items.length === 0) return null;
              return (
                <section key={c.key} data-testid={`store-section-${c.key}`}>
                  <h2 className="text-2xl font-semibold text-[#3A4A38] mb-5">{c.label}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                    {items.map((p) => (
                      <ProductCard key={p.id} product={p} onAdd={() => add(p)} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} onAdd={() => add(p)} />
            ))}
          </div>
        )}
      </div>
      <CartDrawer />
    </div>
  );
}

function CategoryChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
        active
          ? "bg-[#3A4A38] text-white"
          : "bg-white text-[#3A4A38] border border-[#D4C4A8] hover:bg-[#F3F1EB]"
      }`}
    >
      {children}
    </button>
  );
}

function ProductCard({ product, onAdd }) {
  const imgUrl = resolveImageUrl(product.image_url);
  return (
    <motion.article
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2 }}
      data-testid={`store-product-${product.id}`}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#D4C4A8]/30 flex flex-col"
    >
      <div className="aspect-square bg-[#F3F1EB] overflow-hidden">
        {imgUrl ? (
          <img src={imgUrl} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#2C2C2C]/20">
            <ImageIcon size={40} />
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-[#2C2C2C] leading-tight">{product.name}</h3>
        {product.description && (
          <p className="text-xs text-[#2C2C2C]/60 mt-1 line-clamp-2">{product.description}</p>
        )}
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-lg font-semibold text-[#3A4A38]">
            {Number(product.price).toFixed(2)}€
          </span>
          <span className="text-xs text-[#2C2C2C]/60">/ {product.unit}</span>
        </div>
        <button
          onClick={onAdd}
          data-testid={`store-add-${product.id}`}
          className="mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#3A4A38] text-white text-xs font-medium hover:bg-[#2C3A2A] transition-colors"
        >
          <Plus size={14} />
          Adicionar
        </button>
      </div>
    </motion.article>
  );
}
